import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { isEmpty } from "lodash";
import moment from "moment";
import { withTranslation, useTranslation } from "react-i18next";
import { usePreCheckInContext } from "./contexts/PreCheckInContext";
import Booking from "../../model/booking";
import BookingLookupComponent from "./components/BookingLookupComponent";
import BookingInfoImage from "./BookingInfoImage";
import PreCheckInHomeComponent from "./components/PreCheckInHomeComponent";

const PreCheckInHome = ({ bookingType, home }) => {
  const initialFormState = {
    bookingNumber: "",
    userEmail: "",
    lastName: "",
    checkInBookingDate: "",
  };
  const {
    location,
    propertyUid,
    bookingNumber,
    language,
    data: { property, guestService, configuration, presets },
    func: { useFormInput },
    components: { PreCheckInHeader, PreCheckInContainer },
  } = usePreCheckInContext();

  const history = useHistory();
  const { t } = useTranslation();
  const [formData, handleFormChange] = useFormInput(initialFormState);
  const [validateForm, setValidateForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (
      home &&
      !isEmpty(query.get("email")) &&
      !isEmpty(guestService) &&
      guestService.enableAutomaticLookup === true
    ) {
      const pathname = `/pre-checkin/${propertyUid}/upcoming-stays/GuestEmail/${encodeURIComponent(
        query.get("email")
      )}?language=${language}`;
      Booking.setBookingDetailUrl(pathname);
      return history.push(pathname);
    }
    return () => {};
  }, [home, location.search, guestService, history, propertyUid, language]);

  function checkBookingBtn() {
    setSubmitted(true);
    const hasUpcomingStays = ["GuestName", "GuestEmail"].includes(bookingType);
    const bookingPath = guestService?.enableGuestListManagement
      ? "guest-list-management"
      : "book-detail";
    const checkInBookingDateFormat =
      formData.checkInBookingDate &&
      moment(formData.checkInBookingDate).format("YYYY-MM-DD");
    let validateForm = !isEmpty(formData.bookingNumber);
    if (guestService?.enableGuestListManagement) {
      validateForm =
        validateForm &&
        !isEmpty(formData.lastName) &&
        !isEmpty(formData.userEmail);
    } else if (
      bookingType === "BookingNumber" &&
      guestService?.enableLastName
    ) {
      validateForm = validateForm && !isEmpty(formData.lastName);
    }
    if (validateForm) {
      let pathname;
      pathname = !hasUpcomingStays
        ? `/pre-checkin/${propertyUid}/${bookingPath}/${encodeURIComponent(
            formData.bookingNumber
          )}`
        : `/pre-checkin/${propertyUid}/upcoming-stays/${bookingType}/${encodeURIComponent(
            formData.bookingNumber
          )}/${encodeURIComponent(checkInBookingDateFormat)}`;
      if (
        guestService?.enableGuestListManagement ||
        (bookingType === "BookingNumber" && guestService?.enableLastName)
      )
        pathname += `/${encodeURIComponent(formData.lastName)}`;
      pathname += `?language=${language}`;
      if (!isEmpty(formData.userEmail))
        pathname += `&email=${encodeURIComponent(formData.userEmail)}`;
      if (!hasUpcomingStays && !isEmpty(checkInBookingDateFormat))
        pathname += `&arrival_date=${encodeURIComponent(
          checkInBookingDateFormat
        )}`;
      Booking.setBookingDetailUrl(pathname);
      history.push(pathname);
    }
    setValidateForm(validateForm);
  }

  let inputCallToAction = guestService?.inputCallToAction;
  if (bookingType === "GuestName") {
    inputCallToAction = "Last Name";
  } else if (bookingType === "GuestEmail") {
    inputCallToAction = t("EMAIL_ADDRESS");
  }
  const enableBackBtn = !home;
  const enableArrivalDate =
    guestService &&
    guestService.bookingSearchFallbackEnableArrivalDate === true;
  const enableDatePicker =
    ["GuestName", "GuestEmail"].includes(bookingType) || enableArrivalDate;
  const datePickerTitle = enableArrivalDate
    ? t("ARRIVAL_DATE", "Arrival Date")
    : t("CHECK_IN_DATE", "Check-in Date");

  return (
    <>
      <PreCheckInContainer containerName="booking-number">
        <PreCheckInHeader
          title={guestService?.title || t("PRE_CHECK_IN", "PRE CHECK-IN")}
          headerLink={enableBackBtn}
        />
        <section
          id="content"
          className="flex flex-col w-full h-full px-9">
          <div className="flex flex-col justify-center mb-4">
            {guestService?.bookingInfoImage && (
              <div className="-mx-9">
                <BookingInfoImage
                  bookingInfoImage={guestService.bookingInfoImage}
                  propertyName={property.name}
                />
              </div>
            )}
            <div className="w-full flex flex-col mt-6">
              <div className="w-full flex flex-col h-full md:h-auto">
                <div
                  className="uppercase w-full md:text-center mb-4 type-title large"
                  style={{ color: presets?.titleColor }}>
                  {guestService?.bookingSearchFallbackTitle}
                </div>
                <p
                  className="w-full md:text-center leading-1 pt-2 mb-4 type-title medium"
                  style={{ color: presets?.subtitleColor }}>
                  {guestService?.bookingSearchFallbackDescription}
                </p>
                {home ? (
                  <PreCheckInHomeComponent
                    backgroundColorTopBar={presets?.backgroundColorTopBar}
                    appHomeRowDividerColor={presets?.appHomeRowDividerColor}
                    subtitleColor={presets?.subtitleColor}
                    titleColor={presets?.titleColor}
                    history={history}
                    propertyUid={propertyUid}
                    appHomeTextCallToAction={presets?.appHomeTextCallToAction}
                    title={guestService?.bookingSearchFallbackTitle}
                    description={guestService?.bookingSearchFallbackDescription}
                    bookingNumber={bookingNumber}
                    pmsInterfaceType={configuration?.pmsInterfaceType}
                    search={location.search}
                    reservationNumberTitle={t("RESERVATION_NUMBER")}
                    reservationNumberDetail={t("RESERVATION_NUMBER_DETAIL")}
                    emailAddressTitle={t("EMAIL_ADDRESS")}
                    emailAddressDetail={t("EMAIL_ADDRESS_DETAIL")}
                    lastNameTitle={t("LAST_NAME", "Last Name")}
                    lastNameDetail={t(
                      "LAST_NAME_DETAIL",
                      "Tap here to use your last name"
                    )}
                  />
                ) : (
                  <>
                    <BookingLookupComponent
                      backgroundColorTopBar={presets?.backgroundColorTopBar}
                      appHomeTextCallToAction={presets?.appHomeTextCallToAction}
                      input_call_to_action={inputCallToAction}
                      pmsInterfaceType={configuration?.pmsInterfaceType}
                      bookingType={bookingType}
                      handleChange={handleFormChange}
                      datePickerName="checkInBookingDate"
                      datePickerTitle={datePickerTitle}
                      buttonStyleName={presets?.buttonStyleName}
                      enableDatePicker={enableDatePicker}
                      enableLastName={
                        guestService?.enableGuestListManagement ||
                        (bookingType === "BookingNumber" &&
                          guestService?.enableLastName)
                      }
                      enableEmail={guestService?.enableGuestListManagement}
                      mainGuestLastNameTitle={t(
                        "MAIN_GUEST_LAST_NAME",
                        "Main Guest's Last Name"
                      )}
                      mainGuestLastName="lastName"
                      mainGuestEmailTitle={t("YOUR_EMAIL", "Your Email")}
                      mainGuestEmail="userEmail"
                      validateForm={validateForm || !submitted}
                      bookingNumber={formData.bookingNumber}
                      lastName={formData.lastName}
                      userEmail={formData.userEmail}
                      selectedDate={formData.checkInBookingDate}
                      checkBookingBtn={() => checkBookingBtn()}
                      search={location.search}
                    />
                    <button
                      className={`app-button-ipad type-title medium ${presets?.buttonStyleName}`}
                      style={{
                        backgroundColor: presets?.backgroundColorTopBar,
                        color: presets?.appHomeTextCallToAction,
                      }}
                      onClick={() => checkBookingBtn()}>
                      {t("NEXT")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
        {enableBackBtn && (
          <button
            className="app-button-mobile type-title medium "
            style={{
              backgroundColor: presets?.backgroundColorTopBar,
              color: presets?.appHomeTextCallToAction,
              position: "fixed",
              bottom: 0,
              left: 0,
            }}
            onClick={() => checkBookingBtn()}>
            {t("NEXT")}
          </button>
        )}
      </PreCheckInContainer>
    </>
  );
};
export default withTranslation()(PreCheckInHome);
